import React, { useState, useEffect, useRef } from 'react';

const VastPopupPlayer = ({ vastUrl, onClose }) => {
  const [isVisible, setIsVisible] = useState(true);
  const [countdown, setCountdown] = useState(5);
  const [isPlaying, setIsPlaying] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [videoUrl, setVideoUrl] = useState(null);
  const videoRef = useRef(null);
  const timerRef = useRef(null);

  useEffect(() => {
    if (vastUrl) {
      parseVastAndInitialize();
    }

    // Auto-close timer
    timerRef.current = setInterval(() => {
      setCountdown(prev => {
        if (prev <= 1) {
          handleClose();
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }
    };
  }, [vastUrl]);

  const parseVastAndInitialize = async () => {
    try {
      setIsLoading(true);
      setError(null);
      
      console.log('Parsing VAST URL:', vastUrl);
      
      // Simple VAST parsing - try to get video URL
      let finalVideoUrl = null;
      
      try {
        const response = await fetch(vastUrl);
        if (!response.ok) {
          throw new Error(`VAST fetch failed: ${response.status}`);
        }
        
        const vastXml = await response.text();
        console.log('VAST XML received:', vastXml.substring(0, 200) + '...');
        
        // Parse XML
        const parser = new DOMParser();
        const xmlDoc = parser.parseFromString(vastXml, 'application/xml');
        
        // Check for parse errors
        const parseError = xmlDoc.querySelector('parsererror');
        if (parseError) {
          throw new Error('Invalid VAST XML format');
        }
        
        // Look for media files (try multiple approaches)
        let mediaFiles = xmlDoc.querySelectorAll('MediaFile');
        if (mediaFiles.length === 0) {
          mediaFiles = xmlDoc.querySelectorAll('mediaFile');
        }
        
        console.log('Found media files:', mediaFiles.length);
        
        // Find MP4 video
        for (const mediaFile of mediaFiles) {
          const type = mediaFile.getAttribute('type') || '';
          const url = mediaFile.textContent?.trim();
          
          if (url && (type.includes('mp4') || url.includes('.mp4'))) {
            finalVideoUrl = url;
            console.log('Selected MP4 URL:', finalVideoUrl);
            break;
          }
        }
        
        // Fallback to any video file
        if (!finalVideoUrl && mediaFiles.length > 0) {
          const firstMedia = mediaFiles[0];
          finalVideoUrl = firstMedia.textContent?.trim();
          console.log('Using first media file:', finalVideoUrl);
        }
        
      } catch (xmlError) {
        console.log('VAST parsing failed, treating URL as direct video:', xmlError.message);
        // If VAST parsing fails, try using the URL directly as a video source
        finalVideoUrl = vastUrl;
      }
      
      if (finalVideoUrl) {
        setVideoUrl(finalVideoUrl);
        setIsPlaying(true);
      } else {
        throw new Error('No playable video URL found');
      }
      
    } catch (error) {
      console.error('Error parsing VAST:', error);
      setError(error.message);
    } finally {
      setIsLoading(false);
    }
  };

  const parseVastXml = async (url, depth = 0, maxDepth = 5) => {
    if (depth >= maxDepth) {
      throw new Error('Maximum VAST wrapper depth reached');
    }
    
    try {
      console.log(`Parsing VAST (depth ${depth}):`, url);
      
      // Fetch VAST XML
      const response = await fetch(url);
      if (!response.ok) {
        throw new Error(`Failed to fetch VAST: ${response.status}`);
      }
      
      const vastXml = await response.text();
      const parser = new DOMParser();
      const xmlDoc = parser.parseFromString(vastXml, 'application/xml');
      
      // Check for parser errors
      const parseError = xmlDoc.querySelector('parsererror');
      if (parseError) {
        throw new Error('Invalid VAST XML');
      }
      
      // Look for wrapper ads first
      const wrapperAds = xmlDoc.querySelectorAll('Wrapper');
      if (wrapperAds.length > 0) {
        const wrapper = wrapperAds[0];
        const vastAdTagUri = wrapper.querySelector('VASTAdTagURI');
        
        if (vastAdTagUri) {
          const nextUrl = vastAdTagUri.textContent.trim();
          console.log(`Found wrapper, following to: ${nextUrl}`);
          return await parseVastXml(nextUrl, depth + 1, maxDepth);
        }
      }
      
      // Look for linear ads (final video content)
      const linearAds = xmlDoc.querySelectorAll('Linear');
      if (linearAds.length > 0) {
        const linear = linearAds[0];
        
        // Try to find MP4 media files
        const mediaFiles = linear.querySelectorAll('MediaFile');
        let bestVideoUrl = null;
        
        for (const mediaFile of mediaFiles) {
          const type = mediaFile.getAttribute('type');
          const url = mediaFile.textContent.trim();
          
          // Prefer MP4 files
          if (type && type.includes('mp4') && url) {
            bestVideoUrl = url;
            break;
          }
          
          // Fallback to any video file
          if (!bestVideoUrl && url && type && type.startsWith('video/')) {
            bestVideoUrl = url;
          }
        }
        
        if (bestVideoUrl) {
          console.log('Found final video URL:', bestVideoUrl);
          return bestVideoUrl;
        }
      }
      
      throw new Error('No playable media found in VAST');
      
    } catch (error) {
      console.error(`Error parsing VAST at depth ${depth}:`, error);
      throw error;
    }
  };

  const handleClose = () => {
    setIsVisible(false);
    if (timerRef.current) {
      clearInterval(timerRef.current);
    }
    setTimeout(() => {
      onClose();
    }, 300); // Allow fade out animation
  };

  if (!isVisible) {
    return null;
  }

  return (
    <>
      {/* Backdrop */}
      <div className="fixed inset-0 z-50 bg-black bg-opacity-50 backdrop-blur-sm transition-opacity duration-300" />
      
      {/* Popup Container */}
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
        <div className="relative bg-black rounded-lg shadow-2xl border border-gray-600 overflow-hidden animate-in fade-in zoom-in-95 duration-300">
          {/* Close Button */}
          <button
            onClick={handleClose}
            className="absolute top-2 right-2 z-10 w-8 h-8 bg-red-600 hover:bg-red-700 text-white rounded-full flex items-center justify-center text-lg font-bold transition-colors duration-200 shadow-lg"
            aria-label="Close ad"
          >
            ×
          </button>
          
          {/* Countdown Badge */}
          <div className="absolute top-2 left-2 z-10 bg-black bg-opacity-70 text-white px-2 py-1 rounded text-sm font-medium">
            Auto-close in {countdown}s
          </div>
          
          {/* VAST Ad Player */}
          <div 
            className="w-[400px] h-[225px] bg-gray-900 relative"
            style={{ aspectRatio: '400/225' }}
          >
            {isLoading && (
              <div className="absolute inset-0 flex items-center justify-center text-white">
                <div className="text-center">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-white mx-auto mb-2"></div>
                  <div className="text-sm">Parsing VAST XML...</div>
                </div>
              </div>
            )}
            
            {error && (
              <div className="absolute inset-0 flex items-center justify-center text-white">
                <div className="text-center p-4">
                  <div className="text-red-400 text-lg mb-2">⚠️</div>
                  <div className="text-sm">VAST Error</div>
                  <div className="text-xs opacity-70 mt-1">{error}</div>
                </div>
              </div>
            )}
            
            {videoUrl && !isLoading && !error && (
              <video
                ref={videoRef}
                className="w-full h-full object-cover"
                autoPlay
                muted
                controls
                onLoadedData={() => {
                  setIsPlaying(true);
                  console.log('Video loaded successfully');
                }}
                onError={(e) => {
                  console.error('Video error:', e.target.error);
                  setError(`Video playback failed: ${e.target.error?.message || 'Unknown error'}`);
                }}
                onCanPlay={() => console.log('Video can start playing')}
              >
                <source src={videoUrl} type="video/mp4" />
                <source src={videoUrl} type="video/webm" />
                <source src={videoUrl} type="video/ogg" />
                Your browser does not support the video tag.
              </video>
            )}
          </div>
          
          {/* Ad Info */}
          <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black to-transparent p-3">
            <div className="text-white text-xs opacity-90">
              <div className="flex items-center justify-between">
                <span>🎯 Video Advertisement</span>
                <span className="text-orange-400">Sponsored</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default VastPopupPlayer;